import React, { useEffect, useState } from 'react';

import { ConfirmationPrompt, FilterableList, Modal, Notification, STATUS_TYPES, generateResolver, yup } from 'dyl-components';

import SequenceTimeline from './SequenceTimeline';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import sequencesActions from 'actions/sequences';
import sequenceTasksActions from 'actions/sequence_tasks';
import { useParams } from 'react-router-dom';
import { Dimmer, Loader, Segment, Modal as SemanticModal } from 'semantic-ui-react';
import contactSequenceActions from 'actions/contact_sequence';
import RescheduleSequenceModal from 'shared/modals/RescheduleSequenceModal';
import contactActions from 'actions/contact';
import historyActions from 'actions/history';
import textActions from "actions/text";
import { convertRecipientContactVariables, convertSenderVariables } from 'utils/TextFormUtils';

const Sequences = () => {
    const { contact_id } = useParams();

    const { sequences, selectedSequenceTasks, sequenceTaskContact, selectedSequenceId, selectedSequenceName, isReadingSequenceContactTasks, isRemovingSequence, isAddingContactToSequence, pipeline_id, account_stage, pipeline_sequence_id } = useSelector((state) => ({
        sequences: state.sequences.sequencesShort || [],
        selectedSequenceTasks: state.sequence_tasks.selectedSequenceTasks,
        sequenceTaskContact: state.sequence_tasks.sequenceTaskContact,
        selectedSequenceId: state.sequence_tasks.selectedSequenceId,
        selectedSequenceName: state.sequence_tasks.selectedSequenceName,
        isReadingSequenceContactTasks: state.sequence_tasks.isReadingSequenceContactTasks,
        isRemovingSequence: state.contact_sequence.isRemovingSequence,
        isAddingContactToSequence: state.sequences.isAddingContactToSequence,

        pipeline_id: state.contact.current_stage?.pipeline_id,
        pipeline_sequence_id: state.contact.current_stage?.sequence_id,
        account_stage: state.contact.current_stage?.account_stage
    }));
    const sequenceTaskType = (() => {
        if (!sequenceTaskContact?.sequence_id) {
            return ""
        }
        if (sequenceTaskContact?.pipeline_category === "customer") {
            return "customer"
        }
        return "module"
    })()
    const { control, handleSubmit, setValue, reset, formState: { isValid, isDirty } } = useForm({
        mode: 'onChange',
        defaultValues: {
            sequence: ''
        },
        resolver: generateResolver({
            sequence: yup.string().required('This field is required')
        })
    });

    const dispatch = useDispatch();

    const [isCollapsed, setIsCollapsed] = useState(false);

    const onToggleCollapsed = () => {
        setIsCollapsed(!isCollapsed);
    }

    const refresh = () => {
        dispatch(sequenceTasksActions.readContactTasks(contact_id)); 
        dispatch(historyActions.readHistory(contact_id))
    }

    const onAddContact = async (data) => {
        const { sequence } = data;
        const sequenceName = sequences?.find((seq) => seq.id === Number(sequence))?.name
        try {
            await dispatch(sequencesActions.addToSequence({ contact_id: Number(contact_id) }, null, sequence));
            Notification.alert('Sequence successfully added!', STATUS_TYPES.SUCCESS);
            await dispatch(historyActions.createHistory({
                activity_type: "sequence",
                from: null,
                to: sequenceName,
                action: "added",
            },"",contact_id));
            refresh();
        } catch (error) {
            console.log(error);
            Notification.alert('Failed to add sequence!', STATUS_TYPES.ERROR);
        }
    }

    const onCancelTask = async (id) => {
        try {
            await dispatch(sequenceTasksActions.complete(id, { status: "canceled", sequence_task_type: sequenceTaskType, pipeline_id: sequenceTaskContact?.pipeline_id  }));
            Notification.alert('Successfully canceled task!', STATUS_TYPES.SUCCESS);
            refresh();
        } catch (error) {
            console.log(error);
            Notification.alert('Failed to cancel task!', STATUS_TYPES.ERROR);
        }
    }

    const onCompleteTask = async (id) => {
        try {
            await dispatch(sequenceTasksActions.complete(id, { status: "completed", sequence_task_type: sequenceTaskType, pipeline_id: sequenceTaskContact?.pipeline_id  }));
            Notification.alert('Successfully completed task!', STATUS_TYPES.SUCCESS);
            refresh();
        } catch (error) {
            console.log(error);
            Notification.alert('Failed to complete task!', STATUS_TYPES.ERROR);
        }
    }

    const fromPhone = useSelector((state) => state?.text?.phones?.[0] || null);
    const userProfile = useSelector(state => state.user.userProfile);
    
    const [isSending, setIsSending] = useState(false);
    const replaceVariables = (text, contact) => {
        if (!text) return text;
        try {
            return convertRecipientContactVariables(convertSenderVariables(text, userProfile || {}), contact);
        } catch (e) {
            console.error('replaceVariables error:', e);
            return text;
        }
    };
    const handleOnTextConfirm = async (updatedTask) => {
        
        try {
            const { contact } = await dispatch(contactActions.readModalContact(contact_id));
            const contactPrimaryPhone = contact?.phone?.data?.find(phone => phone.main)?.phone;
            setIsSending(true);
            const actionData = updatedTask?.action_data;
            const textBody = {
                from: fromPhone,
                to: contactPrimaryPhone || undefined,
                message: replaceVariables(actionData?.message, { contact: contact, phone: contact?.phone?.data}),
                contact_id: Number(contact_id),
                opt_out_message: actionData?.include_opt_out
            };
            await dispatch(textActions.sendText({
                ...textBody, attachments: actionData?.attachments?.map(attachment => ({
                    id: attachment.file_id,
                    url: attachment.url
                }))
            }))
            await dispatch(sequenceTasksActions.complete(updatedTask.id, { status: "completed", sequence_task_type: sequenceTaskType,  pipeline_id: sequenceTaskContact?.pipeline_id }));
            Notification.alert('Successfully sent text!', STATUS_TYPES.SUCCESS);
            refresh();
            dispatch(textActions.refreshTextingComponents({ from: fromPhone, to: contactPrimaryPhone }));
        } catch (error) {
            console.log(error);
            Notification.alert('Failed to send text', STATUS_TYPES.ERROR);
        } finally {
            setIsSending(false);
        }
    }

    useEffect(() => {
        setValue("sequence", selectedSequenceId || '');
    }, [selectedSequenceId, setValue])

    useEffect(() => {
        dispatch(sequencesActions.readShort({ limit: 2500 }));
        dispatch(sequenceTasksActions.readContactTasks(contact_id))
    }, [contact_id, dispatch]);

    const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false)

    const onCancelRemoval = () => { setIsConfirmingRemoval(false); };

    const onRemoveContact = async () => {
        try {
            await dispatch(contactSequenceActions.removeFromContact(Number(contact_id), { pipeline_id, account_stage, pipeline_sequence_id }));
            Notification.alert('Sequence successfully removed!', STATUS_TYPES.SUCCESS);
            onCancelRemoval();
            if (!pipeline_sequence_id) {
                await dispatch(historyActions.createHistory({
                    activity_type: "sequence",
                    action: "removed",
                    from: selectedSequenceName,
                    to: null
                },"",contact_id));
            }
            refresh();
            if (pipeline_sequence_id) {
                dispatch(contactActions.readContact(contact_id));
            }
        } catch (error) {
            console.log(error);
            Notification.alert('Failed to remove sequence!', STATUS_TYPES.ERROR);
        }
    }

    const [sequenceTaskBeingRescheduled, setSequenceTaskBeingRescheduled] = useState(null);
    return (
        <FilterableList
            header={<div>Sequences</div>}
            onToggleCollapsed={onToggleCollapsed}
            listComponent={
                <Segment loading={isReadingSequenceContactTasks || isRemovingSequence || isAddingContactToSequence || isSending} basic style={{ padding: 0 }}>
                    {!isReadingSequenceContactTasks && (
                        <SequenceTimeline
                            control={control}
                            sequenceOptions={sequences.map((sequence) => ({ key: sequence.id, value: sequence.id, text: sequence.name }))}
                            onSave={handleSubmit(onAddContact)}
                            onComplete={onCompleteTask}
                            selectedSequenceTasks={selectedSequenceTasks}
                            selectedSequenceId={selectedSequenceId}
                            selectedSequenceName={selectedSequenceName}
                            clearSequence={reset}
                            onCancel={onCancelTask}
                            canApplySequence={isValid && isDirty}
                            onRemove={() => { setIsConfirmingRemoval(true) }}
                            onSelectSequenceTaskForRescheduling={(taskId) => { setSequenceTaskBeingRescheduled(taskId) }}
                            handleOnTextConfirm={handleOnTextConfirm}
                        />
                    )}
                    <SemanticModal className='ConfirmModal' size='mini' open={isConfirmingRemoval} onClose={onCancelRemoval}>
                        <Modal.Content>
                            <Dimmer active={isRemovingSequence}>
                                <Loader active={isRemovingSequence} inverted />
                            </Dimmer>
                            <ConfirmationPrompt header={'Are you sure?'} subheader={'All upcoming sequence tasks will be removed'} onCancel={onCancelRemoval} onConfirm={onRemoveContact} />
                        </Modal.Content>
                    </SemanticModal>
                    <RescheduleSequenceModal
                        open={sequenceTaskBeingRescheduled}
                        sequenceTaskId={sequenceTaskBeingRescheduled}
                        onClose={() => { setSequenceTaskBeingRescheduled(null) }}
                        refresh={refresh}
                    />
                </Segment>
            }
            isCollapsed={isCollapsed}
        />
    )
}


export default Sequences;
